import React from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mic, MessageCircle, CheckCircle2, Smartphone, IndianRupee, Users, ArrowRight, Menu, X, Package, TrendingDown, Bell, FileSpreadsheet, BarChart3, Truck, Camera, Volume2, Star, Send } from 'lucide-react';

// WhatsApp number for CTA
const WHATSAPP_NUMBER = '916301997540';
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=register`;
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const FeedbackSection = () => {
  const [name, setName] = React.useState('');
  const [contact, setContact] = React.useState('');
  const [rating, setRating] = React.useState(0);
  const [hoverRating, setHoverRating] = React.useState(0);
  const [message, setMessage] = React.useState('');
  const [status, setStatus] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rating) {
      setStatus({ type: 'error', text: 'Please select a star rating.' });
      return;
    }
    setSubmitting(true);
    setStatus(null);
    try {
      await axios.post(`${API_BASE}/api/feedback`, {
        name,
        contact: contact.trim() || null,
        rating,
        message
      });
      setStatus({ type: 'success', text: 'Thank you! Your feedback has been submitted. 🙏' });
      setName('');
      setContact('');
      setRating(0);
      setMessage('');
    } catch (err) {
      setStatus({ type: 'error', text: 'Could not submit feedback. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="feedback" className="py-16 md:py-24 bg-stone-100 scroll-mt-16">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2
            className="text-3xl md:text-4xl font-bold text-stone-800 mb-4"
            style={{ fontFamily: "'Eczar', serif" }}
            data-testid="feedback-heading"
          >
            Share Your Feedback
          </h2>
          <p className="text-lg text-stone-600">
            Using BolkeHisaab? Have a suggestion or question? We'd love to hear from you.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-6 md:p-8 space-y-5" data-testid="feedback-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1.5">Your Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={100}
                placeholder="Ramesh Kumar"
                className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                data-testid="feedback-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1.5">Phone / Email (optional)</label>
              <input
                type="text"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                maxLength={100}
                placeholder="98xxxxxxxx or you@email.com"
                className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                data-testid="feedback-contact-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-stone-700 mb-1.5">Rating *</label>
            <div className="flex items-center gap-1" data-testid="feedback-star-rating">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`feedback-star-${star}`}
                  aria-label={`${star} star`}
                >
                  <Star
                    size={32}
                    className={
                      star <= (hoverRating || rating)
                        ? 'fill-amber-400 text-amber-400'
                        : 'text-stone-300'
                    }
                  />
                </button>
              ))}
              {rating > 0 && <span className="ml-2 text-stone-600 font-medium">{rating}/5</span>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-stone-700 mb-1.5">Your Message *</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              maxLength={2000}
              rows={4}
              placeholder="Tell us what you like, or what we can improve..."
              className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
              data-testid="feedback-message-input"
            />
          </div>

          {status && (
            <div
              className={`px-4 py-3 rounded-lg text-sm font-medium ${
                status.type === 'success'
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}
              data-testid="feedback-status-message"
            >
              {status.text}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 bg-emerald-700 text-white px-6 py-3.5 rounded-full font-bold hover:bg-emerald-800 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="feedback-submit-btn"
          >
            <Send size={18} />
            {submitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </form>
      </div>
    </section>
  );
};

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  return (
    <div className="min-h-screen bg-stone-100" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 
                className="text-xl font-bold text-emerald-700"
                style={{ fontFamily: "'Eczar', serif" }}
              >
                BolkeHisaab
              </h1>
            </div>
            
            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-stone-600 hover:text-emerald-700 font-medium transition">Features</a>
              <a href="#how-it-works" className="text-stone-600 hover:text-emerald-700 font-medium transition">How it Works</a>
              <a href="#inventory" className="text-stone-600 hover:text-emerald-700 font-medium transition">Inventory</a>
              <a href="#feedback" className="text-stone-600 hover:text-emerald-700 font-medium transition">Feedback</a>
              <Link 
                to="/admin" 
                className="text-stone-500 hover:text-emerald-700 font-medium transition"
              >
                Admin
              </Link>
            </nav>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2 text-stone-600"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-stone-200 py-4 px-4">
            <nav className="flex flex-col space-y-4">
              <a href="#features" className="text-stone-600 font-medium" onClick={() => setMobileMenuOpen(false)}>Features</a>
              <a href="#how-it-works" className="text-stone-600 font-medium" onClick={() => setMobileMenuOpen(false)}>How it Works</a>
              <a href="#inventory" className="text-stone-600 font-medium" onClick={() => setMobileMenuOpen(false)}>Inventory</a>
              <a href="#feedback" className="text-stone-600 font-medium" onClick={() => setMobileMenuOpen(false)}>Feedback</a>
              <Link to="/admin" className="text-stone-500 font-medium" onClick={() => setMobileMenuOpen(false)}>Admin</Link>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-center md:text-left">
              <p className="text-sm font-bold uppercase tracking-widest text-emerald-600 mb-4">
                Voice-First Bookkeeping & Inventory
              </p>
              <h1 
                className="text-4xl sm:text-5xl md:text-6xl font-bold text-stone-800 leading-tight mb-6"
                style={{ fontFamily: "'Eczar', serif" }}
                data-testid="hero-heading"
              >
                Ab Hisaab Rakho{' '}
                <span className="text-emerald-700">Bol Kar</span>
              </h1>
              <p className="text-lg md:text-xl text-stone-600 mb-8 leading-relaxed">
                Record sales, track inventory, and manage suppliers — all by voice. 
                Or just snap a photo of your supplier bill. 
                The digital <span className="font-semibold">munshi</span> for smart shopkeepers.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 bg-emerald-700 text-white px-8 py-4 rounded-full text-lg font-bold shadow-lg shadow-emerald-700/20 hover:bg-emerald-800 hover:scale-105 active:scale-95 transition-all"
                  data-testid="hero-whatsapp-btn"
                >
                  <MessageCircle size={24} />
                  Start on WhatsApp
                  <ArrowRight size={20} />
                </a>
                <a
                  href="#how-it-works"
                  className="inline-flex items-center justify-center gap-2 bg-white text-emerald-700 border-2 border-emerald-200 px-8 py-4 rounded-full text-lg font-semibold hover:border-emerald-500 hover:bg-emerald-50 transition-all"
                >
                  See How It Works
                </a>
              </div>
              
              <p className="mt-6 text-sm text-stone-500">
                Free to use. No app download required.
              </p>
            </div>

            {/* Right - Phone Mockup */}
            <div className="relative flex justify-center">
              <div className="relative w-72 md:w-80">
                {/* Phone Frame */}
                <div className="bg-stone-800 rounded-[3rem] p-3 shadow-2xl">
                  <div className="bg-white rounded-[2.5rem] overflow-hidden">
                    {/* WhatsApp Header */}
                    <div className="bg-emerald-700 px-4 py-3 flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-sm">BH</span>
                      </div>
                      <div>
                        <p className="text-white font-semibold text-sm">BolkeHisaab</p>
                        <p className="text-emerald-200 text-xs">online</p>
                      </div>
                    </div>
                    
                    {/* Chat Messages */}
                    <div className="bg-[#e5ddd5] p-3 min-h-[300px] space-y-2">
                      {/* User voice - sale */}
                      <div className="flex justify-end">
                        <div className="bg-[#dcf8c6] rounded-lg px-3 py-2 max-w-[80%] shadow">
                          <div className="flex items-center gap-2 text-emerald-800">
                            <Mic size={16} />
                            <div className="flex gap-0.5">
                              {[...Array(12)].map((_, i) => (
                                <div key={`wave-sale-${i}`} className="w-1 bg-emerald-600 rounded" style={{ height: `${Math.random() * 16 + 4}px` }} />
                              ))}
                            </div>
                            <span className="text-xs">0:05</span>
                          </div>
                          <p className="text-xs text-stone-500 mt-1">Ramesh ko 5 kg rice diya udhar</p>
                        </div>
                      </div>
                      
                      {/* Bot response - sale */}
                      <div className="flex justify-start">
                        <div className="bg-white rounded-lg px-3 py-2 max-w-[85%] shadow">
                          <p className="text-xs text-stone-800">
                            <span className="text-emerald-600 font-medium">Sale recorded!</span>
                          </p>
                          <p className="text-xs text-stone-600 mt-0.5">
                            Ramesh: 5 kg Rice (Credit)
                          </p>
                        </div>
                      </div>

                      {/* User voice - stock */}
                      <div className="flex justify-end">
                        <div className="bg-[#dcf8c6] rounded-lg px-3 py-2 max-w-[80%] shadow">
                          <div className="flex items-center gap-2 text-emerald-800">
                            <Mic size={16} />
                            <div className="flex gap-0.5">
                              {[...Array(10)].map((_, i) => (
                                <div key={i} className="w-1 bg-emerald-600 rounded" style={{ height: `${Math.random() * 14 + 4}px` }} />
                              ))}
                            </div>
                            <span className="text-xs">0:04</span>
                          </div>
                          <p className="text-xs text-stone-500 mt-1">Amul se 10 carton butter aaya</p>
                        </div>
                      </div>
                      
                      {/* Bot response - stock */}
                      <div className="flex justify-start">
                        <div className="bg-white rounded-lg px-3 py-2 max-w-[85%] shadow">
                          <p className="text-xs text-stone-800">
                            <span className="text-emerald-600 font-medium">Stock added!</span>
                          </p>
                          <p className="text-xs text-stone-600 mt-0.5">
                            Amul Butter: +10 cartons from Amul
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Decorative elements */}
                <div className="absolute -top-4 -right-4 w-20 h-20 bg-orange-400 rounded-full opacity-20 blur-xl" />
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-emerald-400 rounded-full opacity-20 blur-xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Problem */}
            <div className="bg-stone-50 rounded-2xl p-8 border border-stone-200">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                <X className="text-red-600" size={24} />
              </div>
              <h3 className="text-2xl font-bold text-stone-800 mb-4" style={{ fontFamily: "'Eczar', serif" }}>
                The Old Way
              </h3>
              <ul className="space-y-3 text-stone-600">
                <li className="flex items-start gap-3">
                  <span className="text-red-500 mt-1">&#10007;</span>
                  Paper ledgers that get lost or damaged
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 mt-1">&#10007;</span>
                  Manual stock counting — always outdated
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 mt-1">&#10007;</span>
                  No idea which items are running low
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 mt-1">&#10007;</span>
                  Typing in apps is slow and confusing
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 mt-1">&#10007;</span>
                  Language barriers with English-only apps
                </li>
              </ul>
            </div>

            {/* Solution */}
            <div className="bg-emerald-50 rounded-2xl p-8 border border-emerald-200">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                <CheckCircle2 className="text-emerald-600" size={24} />
              </div>
              <h3 className="text-2xl font-bold text-stone-800 mb-4" style={{ fontFamily: "'Eczar', serif" }}>
                The BolkeHisaab Way
              </h3>
              <ul className="space-y-3 text-stone-600">
                <li className="flex items-start gap-3">
                  <span className="text-emerald-600 mt-1">&#10003;</span>
                  Just speak — in Hindi, Telugu, or English
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-emerald-600 mt-1">&#10003;</span>
                  Stock auto-updates with every sale and delivery
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-emerald-600 mt-1">&#10003;</span>
                  Low stock alerts — never run out again
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-emerald-600 mt-1">&#10003;</span>
                  Works on WhatsApp — no new app needed
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-emerald-600 mt-1">&#10003;</span>
                  Daily summary and CSV reports on demand
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-16 md:py-24 bg-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-bold uppercase tracking-widest text-emerald-600 mb-4">
              Simple Process
            </p>
            <h2 
              className="text-3xl md:text-4xl font-bold text-stone-800"
              style={{ fontFamily: "'Eczar', serif" }}
            >
              Start in 3 Easy Steps
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="bg-white rounded-2xl p-8 border border-stone-200 shadow-sm hover:shadow-md transition-all text-center group">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <Mic className="text-emerald-700" size={28} />
              </div>
              <div className="text-5xl font-bold text-emerald-200 mb-4" style={{ fontFamily: "'Eczar', serif" }}>01</div>
              <h3 className="text-xl font-bold text-stone-800 mb-3">Speak</h3>
              <p className="text-stone-600">
                Press the mic button on WhatsApp and speak your transaction or stock update naturally
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-2xl p-8 border border-stone-200 shadow-sm hover:shadow-md transition-all text-center group">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <MessageCircle className="text-orange-600" size={28} />
              </div>
              <div className="text-5xl font-bold text-orange-200 mb-4" style={{ fontFamily: "'Eczar', serif" }}>02</div>
              <h3 className="text-xl font-bold text-stone-800 mb-3">Send</h3>
              <p className="text-stone-600">
                Send the voice note in Hindi, Telugu, English — mix and match!
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-2xl p-8 border border-stone-200 shadow-sm hover:shadow-md transition-all text-center group">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <CheckCircle2 className="text-emerald-700" size={28} />
              </div>
              <div className="text-5xl font-bold text-emerald-200 mb-4" style={{ fontFamily: "'Eczar', serif" }}>03</div>
              <h3 className="text-xl font-bold text-stone-800 mb-3">Done!</h3>
              <p className="text-stone-600">
                Sales, stock, and supplier data saved instantly — with auto inventory deduction
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-bold uppercase tracking-widest text-emerald-600 mb-4">
              Features
            </p>
            <h2 
              className="text-3xl md:text-4xl font-bold text-stone-800"
              style={{ fontFamily: "'Eczar', serif" }}
            >
              Built for Indian Shopkeepers
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Mic, title: 'Multi-Language Voice', desc: 'Speak in Telugu, Hindi, Dakhni, English — we understand it all', color: 'emerald' },
              { icon: Smartphone, title: 'WhatsApp Based', desc: 'No new app to download. Works on the app you already use daily', color: 'orange' },
              { icon: IndianRupee, title: 'Credit Tracking', desc: 'Automatic udhar tracking with customer-wise balances', color: 'emerald' },
              { icon: Users, title: 'Customer Management', desc: 'Auto-creates customers from voice. Tracks history automatically', color: 'orange' },
              { icon: Volume2, title: 'Voice + Text Replies', desc: 'Get every confirmation as a voice note AND text — listen, don\'t read', color: 'emerald' },
              { icon: FileSpreadsheet, title: 'Download Reports', desc: 'Say "download" or "stock report bhejo" to get CSV exports instantly', color: 'orange' },
            ].map((feature, idx) => (
              <div 
                key={feature.title} 
                className="bg-stone-50 rounded-2xl p-6 border border-stone-200 hover:border-emerald-300 hover:shadow-md transition-all group"
                data-testid={`feature-card-${idx}`}
              >
                <div className={`w-12 h-12 ${feature.color === 'emerald' ? 'bg-emerald-100' : 'bg-orange-100'} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <feature.icon className={feature.color === 'emerald' ? 'text-emerald-700' : 'text-orange-600'} size={24} />
                </div>
                <h3 className="text-lg font-bold text-stone-800 mb-2">{feature.title}</h3>
                <p className="text-stone-600 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inventory Section */}
      <section id="inventory" className="py-16 md:py-24 bg-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-bold uppercase tracking-widest text-orange-600 mb-4">
              Inventory Management
            </p>
            <h2 
              className="text-3xl md:text-4xl font-bold text-stone-800"
              style={{ fontFamily: "'Eczar', serif" }}
              data-testid="inventory-section-heading"
            >
              Your Complete Stock Manager
            </h2>
            <p className="mt-4 text-base md:text-lg text-stone-600 max-w-2xl mx-auto">
              Track every carton, every kilo, every piece — just by speaking. Your inventory stays accurate without lifting a pen.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Package,
                title: 'Auto Stock Deduction',
                desc: 'Every sale you record automatically deducts from inventory. No double entry, ever.',
                color: 'emerald',
                example: '"Ramesh ne 5 kg rice liya" → Stock: 50kg → 45kg'
              },
              {
                icon: Truck,
                title: 'Supplier Stock-In',
                desc: 'Record supplier deliveries by voice. Auto-creates suppliers and products if new.',
                color: 'orange',
                example: '"Amul se 10 carton butter aaya"'
              },
              {
                icon: TrendingDown,
                title: 'Stock Adjustments',
                desc: 'Add, reduce, or adjust stock for damage, returns, or corrections — all by voice.',
                color: 'emerald',
                example: '"5 piece Biscoff damage hua, nikalo"'
              },
              {
                icon: Bell,
                title: 'Low Stock Alerts',
                desc: 'Get warned automatically when items run low after a sale or stock reduction.',
                color: 'orange',
                example: 'Warning: Rice stock low! Only 2kg left'
              },
              {
                icon: BarChart3,
                title: 'Daily Summary at 9 PM',
                desc: 'Receive your full business summary on WhatsApp every evening — sales, pending, low stock.',
                color: 'emerald',
                example: 'Today: 12 sales, Rs.15,000, 3 items low'
              },
              {
                icon: FileSpreadsheet,
                title: 'Inventory Reports',
                desc: 'Download stock levels, supplier purchases, and movements as CSV — anytime.',
                color: 'orange',
                example: '"mera stock report bhejo"'
              },
            ].map((feature, idx) => (
              <div 
                key={feature.title} 
                className="bg-white rounded-2xl p-6 border border-stone-200 hover:border-orange-300 hover:shadow-md transition-all group"
                data-testid={`inventory-feature-${idx}`}
              >
                <div className={`w-12 h-12 ${feature.color === 'emerald' ? 'bg-emerald-100' : 'bg-orange-100'} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <feature.icon className={feature.color === 'emerald' ? 'text-emerald-700' : 'text-orange-600'} size={24} />
                </div>
                <h3 className="text-lg font-bold text-stone-800 mb-2">{feature.title}</h3>
                <p className="text-stone-600 text-sm mb-3">{feature.desc}</p>
                <div className="bg-stone-50 rounded-lg px-3 py-2 border border-stone-100">
                  <p className="text-xs text-stone-500 italic">{feature.example}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Supported Units */}
          <div className="mt-12 bg-white rounded-2xl p-8 border border-stone-200">
            <h3 className="text-lg font-bold text-stone-800 mb-4 text-center" style={{ fontFamily: "'Eczar', serif" }}>
              Works with All Unit Types
            </h3>
            <div className="flex flex-wrap justify-center gap-3">
              {['Cartons', 'Pieces', 'Kg', 'Litres', 'Grams', 'Packets', 'Boxes', 'Loose Items'].map((unit) => (
                <span 
                  key={unit}
                  className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-4 py-2 rounded-full text-sm font-medium"
                >
                  {unit}
                </span>
              ))}
            </div>
            <p className="text-center text-stone-500 text-sm mt-4">
              Carton-based items automatically track loose pieces too. Smart carton-breaking when loose stock runs out.
            </p>
          </div>
        </div>
      </section>

      {/* Photo-to-Voice Bill Scan Section */}
      <section id="bill-scan" className="py-16 md:py-24 bg-emerald-900 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <span className="inline-block bg-orange-500 text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
              New
            </span>
            <h2 
              className="text-3xl md:text-4xl font-bold text-white"
              style={{ fontFamily: "'Eczar', serif" }}
              data-testid="bill-scan-heading"
            >
              Bill Ki Photo Bhejo, Stock Apne Aap
            </h2>
            <p className="mt-4 text-base md:text-lg text-emerald-100 max-w-2xl mx-auto">
              Distributor ka printed bill aaya? 20 items voice se bolne ki zaroorat nahi. 
              Bas photo khinch kar WhatsApp par bhejo.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                icon: Camera,
                step: '1',
                title: 'Snap the Bill',
                desc: 'Take a photo of your printed distributor bill and send it on WhatsApp.',
              },
              {
                icon: CheckCircle2,
                step: '2',
                title: 'We Read Every Item',
                desc: 'AI extracts every product, brand, quantity and price — and reads the list back to you.',
              },
              {
                icon: Mic,
                step: '3',
                title: 'Say "Haan"',
                desc: 'Confirm by voice and all items are added to your stock. Correct any item by just speaking.',
              },
            ].map((item, idx) => (
              <div 
                key={idx} 
                className="bg-emerald-800/60 backdrop-blur rounded-2xl p-6 border border-emerald-700 text-center"
                data-testid={`bill-scan-step-${idx}`}
              >
                <div className="w-14 h-14 bg-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="text-white" size={26} />
                </div>
                <p className="text-orange-300 text-sm font-bold mb-2">STEP {item.step}</p>
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-emerald-100 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>

          <p className="text-center text-emerald-200 text-sm mt-10">
            Nothing is added to stock until you confirm — you're always in control. Works with printed bills.
          </p>
        </div>
      </section>

      {/* Voice Commands Showcase */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-bold uppercase tracking-widest text-emerald-600 mb-4">
              Just Speak
            </p>
            <h2 
              className="text-3xl md:text-4xl font-bold text-stone-800"
              style={{ fontFamily: "'Eczar', serif" }}
            >
              One Voice, Everything Managed
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              { category: 'Customer Sales', color: 'emerald', commands: [
                '"Ramesh ne 5 kg rice liya"',
                '"Suresh se 1000 rupay mila cash"',
                '"Ali bhai 3 packet biscuit le gaya"',
              ]},
              { category: 'Stock & Inventory', color: 'orange', commands: [
                '"Amul se 10 carton butter aaya"',
                '"5 carton olives add karo"',
                '"3 piece Biscoff damage, nikalo"',
              ]},
              { category: 'Queries', color: 'emerald', commands: [
                '"Rice kitna hai stock mein?"',
                '"Ramesh ka balance batao"',
                '"Aaj ka hisab batao"',
              ]},
              { category: 'Reports & Exports', color: 'orange', commands: [
                '"Mera stock report bhejo"',
                '"Download today transactions"',
                '"Supplier purchases ka CSV do"',
              ]},
            ].map((group, idx) => (
              <div key={idx} className={`rounded-2xl p-6 border ${group.color === 'emerald' ? 'bg-emerald-50 border-emerald-200' : 'bg-orange-50 border-orange-200'}`}>
                <h3 className={`text-base font-bold mb-3 ${group.color === 'emerald' ? 'text-emerald-700' : 'text-orange-700'}`}>
                  {group.category}
                </h3>
                <ul className="space-y-2">
                  {group.commands.map((cmd, i) => (
                    <li key={cmd} className="flex items-center gap-2 text-sm text-stone-700">
                      <Mic size={14} className="text-stone-400 flex-shrink-0" />
                      <span>{cmd}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feedback Section */}
      <FeedbackSection />

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-emerald-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl md:text-4xl font-bold text-white mb-6"
            style={{ fontFamily: "'Eczar', serif" }}
          >
            Ready to Simplify Your Business?
          </h2>
          <p className="text-lg text-emerald-100 mb-8 max-w-2xl mx-auto">
            Join shopkeepers who manage sales, inventory, and suppliers — all by voice on WhatsApp. 
            Start for free, no credit card required.
          </p>
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-3 bg-white text-emerald-700 px-10 py-5 rounded-full text-xl font-bold shadow-lg hover:bg-emerald-50 hover:scale-105 active:scale-95 transition-all"
            data-testid="cta-whatsapp-btn"
          >
            <MessageCircle size={28} />
            Start on WhatsApp Now
            <ArrowRight size={24} />
          </a>
          <p className="mt-6 text-emerald-200 text-sm">
            Message "register" to get started instantly
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-center md:text-left">
              <h3 
                className="text-xl font-bold text-white mb-2"
                style={{ fontFamily: "'Eczar', serif" }}
              >
                BolkeHisaab
              </h3>
              <p className="text-stone-400 text-sm">
                Voice-native bookkeeping & inventory for Indian MSMEs
              </p>
            </div>
            
            <div className="flex items-center gap-6 text-sm">
              <a href="#" className="text-stone-400 hover:text-white transition">Privacy</a>
              <a href="#" className="text-stone-400 hover:text-white transition">Terms</a>
              <Link to="/admin" className="text-stone-400 hover:text-white transition">Admin Login</Link>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-stone-700 text-center">
            <p className="text-stone-500 text-sm">
              &copy; {new Date().getFullYear()} BolkeHisaab by Zentropy AI. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;

